import config from '@/amplify_outputs.json'

type AmplifyDataAuthConfig = {
  default_authorization_type?: string
  authorization_types?: string[]
}

type AmplifyAuthConfig = {
  data?: AmplifyDataAuthConfig
}

type LambdaServerGraphqlAuthOptions =
  | {
      authMode: 'lambda'
      authToken: string
    }
  | Record<string, never>

type LambdaClientGraphqlAuthOptions = {
  authMode: 'lambda'
  headers: () => Promise<{ Authorization: string }>
}

const AWS_LAMBDA_AUTH_TYPE = 'AWS_LAMBDA'

export const supportsLambdaAuthorizer = (
  amplifyConfig: AmplifyAuthConfig = config,
): boolean => {
  const defaultAuthType = amplifyConfig.data?.default_authorization_type
  const authorizationTypes = amplifyConfig.data?.authorization_types ?? []

  return (
    defaultAuthType === AWS_LAMBDA_AUTH_TYPE ||
    authorizationTypes.includes(AWS_LAMBDA_AUTH_TYPE)
  )
}

export const getClientGraphqlAuthOptions = (
  getAuthToken: () => Promise<string | undefined>,
  amplifyConfig: AmplifyAuthConfig = config,
): LambdaClientGraphqlAuthOptions | undefined => {
  if (!supportsLambdaAuthorizer(amplifyConfig)) return undefined

  return {
    authMode: 'lambda',
    headers: async () => {
      try {
        const authToken = await getAuthToken()

        // AppSync lambda auth requires an Authorization header, even for guest reads.
        // The authorizer treats missing/empty/'null' tokens as guest context.
        return { Authorization: authToken ?? 'null' }
      } catch {
        return { Authorization: 'null' }
      }
    },
  }
}

export const getServerGraphqlAuthOptions = (
  authToken?: string | null,
  amplifyConfig: AmplifyAuthConfig = config,
): LambdaServerGraphqlAuthOptions => {
  if (!supportsLambdaAuthorizer(amplifyConfig)) return {}

  return {
    authMode: 'lambda',
    authToken: authToken ?? 'null',
  }
}
